import { Injectable }                     from '@angular/core';
import { BusinessPremisesPresetEnum }     from '@app/modules/business-premises/models/business-premises.model';
import { ORGANIZATION_METADATA }          from '@app/modules/organization/config/organization.config';
import {
	Organization,
	OrganizationStatusEnum
}                                         from '@app/modules/organization/models/organization.model';
import {
	OrganizationDetailPageBusinessPremisesTabVariantRoute,
	OrganizationDetailPageRoute,
	OrganizationDetailPageTabRoute,
	OrganizationFormPageCreateRoute,
	OrganizationFormPageUpdateRoute,
	OrganizationListPageRoute,
	OrganizationRoutesEnum,
	OrganizationUsagePageRoute
}                                         from '@app/modules/organization/routes/organization.routes';
import { OrganizationHelperCheckService } from '@app/modules/organization/services/organization-helper-check.service';
import { IPropsUpdateOrganizationState }  from '@app/modules/organization/store/organization.actions';
import { organizationStore }              from '@app/modules/organization/store/organization.store';
import { ImportTypeEnum }                 from '@app/modules/structure-import/models/structure-import.model';
import { getYearMonth }                   from '@app/modules/usage/helpers/usage-month.helper';
import { AbstractBaseService }            from '@core/bases/services/abstractBaseService';
import {
	IOrganizationHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                         from '@core/interfaces/api.interface';
import {
	AllowedDetailsTabEnum,
	ProductEnum
}                                         from '@core/interfaces/common.interfaces';
import { RouteBasePath }                  from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	StoreActionsEnum
}                                         from '@core/interfaces/store.interface';
import { OrganizationApiService }         from '@http/organizations/organization-api.service';
import { IHttpContextConfig }             from '@interceptors/interceptors.model';
import { Store }                          from '@ngrx/store';
import {
	IChangeStatusOrganizationCallbackOptions,
	IStatusFlowChangeEvent
}                                         from '@shared/components/status-flow/status-flow.model';
import { StatusFlowService }              from '@shared/components/status-flow/status-flow.service';
import { ListOperationsIdentifierEnum }   from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                   from '@shared/services/toaster.service';
import { Observable }                     from 'rxjs';
import { take }                           from 'rxjs/operators';

@Injectable( { providedIn: 'root' } )
export class OrganizationService extends AbstractBaseService {

	readonly organizationMetadata = ORGANIZATION_METADATA;

	constructor(
		private readonly _organizationApiService: OrganizationApiService,
		private readonly _organizationHelperCheckService: OrganizationHelperCheckService,
		private readonly _statusFlowService: StatusFlowService,
		protected readonly store$: Store
	) {
		super( store$, organizationStore.selectors, organizationStore.actions );

		this.logger.trace( 'OrganizationService:constructor' );
		this._organizationHelperCheckService.organizationServiceInstance = this;
		this._organizationApiService.organizationServiceInstance = this;
	}

	get check(): OrganizationHelperCheckService {
		return this._organizationHelperCheckService;
	}


	// ACTIONS ###########################################################################
	private changeOrganizationStatus(
		callbackOptions: IChangeStatusOrganizationCallbackOptions,
		statusChangeEvent: IStatusFlowChangeEvent<OrganizationStatusEnum>
	) {
		this._statusFlowService.changeStatus(
			{
				callback: this.changeStatusCallback,
				callbackOptions,
				caller: callbackOptions?.caller,
				entity: callbackOptions?.entity,
				entityId: this.organizationMetadata?.entityId,
				entityService: this,
				statusChangeEvent
			}
		);
	}

	private changeStatusCallback(
		service: OrganizationService,
		callbackOptions: IChangeStatusOrganizationCallbackOptions
	) {
		const {
			caller,
			entity,
			listOperationsId
		} = { ...callbackOptions };

		const organization: Organization = entity as Organization;

		if ( organization ) {
			if ( listOperationsId ) {
				service.uiService.showPageLoaderAndCountUp();
				service.searchWithStoredListOperationsData(
					listOperationsId,
					false,
					'OrganizationService:changeStatusCallback'
				)
				.pipe( take( 1 ) )
				.subscribe( ( orgListRefreshState: RequestCompleteListState<Organization> ) => {
					service.uiService.hidePageLoaderOrCountDown();
				} );
			} else {
				service.checkAndUpdateEntityState(
					organization,
					caller
				);
			}
		}
	}

	private deleteOrganization( organizationId: string, navigateToList: boolean, caller: string ): void {
		const dialogConfirmationText = this.translationService.getI18nString(
			true,
			ORGANIZATION_METADATA.i18n.basepath,
			'entity.dialogForDelete.description'
		);
		this.dialogCommonService.showConfirmationDialog( dialogConfirmationText, 'delete' )
		.pipe( take( 1 ) )
		.subscribe( res => {
			this.uiService.showPageLoaderAndCountUp();
			this.delete( organizationId )
			.pipe( take( 1 ) )
			.subscribe( ( orgState: RequestCompleteEntityState<Organization> ) => {
				if ( orgState.requestState === RequestState.SUCCESS ) {
					this.storeRemoveOne<Organization>( organizationId );
				}
				this.toasterService.dispatch( orgState, this.organizationMetadata.entityId, actionStatus.DELETE );
				this.uiService.hidePageLoaderOrCountDown();
				if ( navigateToList ) { this.navigateToListPage(); }
			} );
		} );
	}

	private pageReadOrganizationHandler( readReq$: Observable<RequestCompleteEntityState<Organization>> ): void {
		readReq$
		.pipe( take( 1 ) )
		.subscribe( ( organizationState: RequestCompleteEntityState<Organization> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			// on fail redirects to list
			if ( organizationState.requestState === RequestState.FAILED ) {
				this.toasterService.dispatch( organizationState, this.organizationMetadata.entityId, actionStatus.READ );
				this.navigateToListPage();
			}
		} );
	}


	readOrganizationOnDetailsPage( organizationId: string ): void {
		this.uiService.showPageLoaderAndCountUp();
		const readReq$ = this.read( { id: organizationId, skipOrganizationActionItemChecks: false } );
		this.pageReadOrganizationHandler( readReq$ );
	}

	readOrganizationOnFormPage( organizationId: string ): void {
		this.uiService.showPageLoaderAndCountUp();
		const readReq$ = this.read( { id: organizationId, skipOrganizationActionItemChecks: true, withFullResponse: true } );
		this.pageReadOrganizationHandler( readReq$ );
	}


	showStructureImportForOrganization(): void {
		this.store$.dispatch(
			organizationStore.actions.structureImportForOrganization(
				{
					caller: 'OrganizationService:showImport',
					importType: ImportTypeEnum.ORGANIZATION
				}
			)
		);
	}

	showStructureExportForOrganization( organization: Organization, caller: string ): void {
		this.store$.dispatch(
			organizationStore.actions.structureExportForOrganization(
				{ organization, caller: caller + ' | OrganizationService:showStructureExportForOrganization' }
			)
		);
	}


	deleteOrganizationOnDetailPage( organizationId: string, caller: string ): void {
		this.deleteOrganization( organizationId, true, caller );
	}

	deleteOrganizationOnEntity( organizationId: string, caller: string ): void {
		this.deleteOrganization( organizationId, false, caller );
	}


	changeOrganizationStatusOnDetailPage(
		organization: Organization,
		caller: string,
		statusChangeEvent: IStatusFlowChangeEvent<OrganizationStatusEnum>
	): void {
		this.changeOrganizationStatus(
			{
				caller,
				entity: organization
			},
			statusChangeEvent
		);
	}

	changeOrganizationStatusOnEntity(
		organization: Organization,
		caller: string,
		statusChangeEvent: IStatusFlowChangeEvent<OrganizationStatusEnum>
	): void {
		this.changeOrganizationStatus(
			{
				caller,
				entity: organization,
				listOperationsId: ListOperationsIdentifierEnum.ORGANIZATION_LIST_PAGE
			},
			statusChangeEvent
		);
	}


	// CHECKS #################################################################################
	checkAndUpdateEntityState( organization: Organization, caller: string ): void {
		caller += ' | OrganizationService:checkAndUpdateEntityState';
		this.store$.dispatch(
			organizationStore.actions.hasActiveBusinessPremisesChildren( { organization, caller } )
		);
	}


	// STORE ###########################################################################
	// SELECTORS
	// UPDATERS
	updateStoreState( updateOrganizationState: IPropsUpdateOrganizationState ): void {
		this.store$.dispatch( organizationStore.actions.updateOrganizationState( updateOrganizationState ) );
	}


	// NAVIGATION #################################################################################
	navigateToListPage(): void {
		const routeDefinition: OrganizationListPageRoute = {
			basePath: RouteBasePath.Organizations,
			path: OrganizationRoutesEnum.ListPage
		};

		this.routerService.navigateToNextRoute(
			routeDefinition,
			'OrganizationService:navigateToListPage'
		);
	}

	navigateToDetailPage( organizationId: string ): void {
		if ( organizationId ) {
			const routeDefinition: OrganizationDetailPageRoute = {
				basePath: RouteBasePath.Organizations,
				path: OrganizationRoutesEnum.DetailPage,
				params: { organizationId }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'OrganizationService:navigateToDetailPage',
				true
			);
		}
	}

	navigateToFormCreate(): void {
		const routeDefinition: OrganizationFormPageCreateRoute = {
			basePath: RouteBasePath.Organizations,
			path: OrganizationRoutesEnum.FormPageCreate
		};

		this.routerService.navigateToNextRoute(
			routeDefinition,
			'OrganizationService:navigateToFormCreate'
		);
	}

	navigateToFormUpdate( organizationId: string ): void {

		if ( organizationId ) {
			const routeDefinition: OrganizationFormPageUpdateRoute = {
				basePath: RouteBasePath.Organizations,
				path: OrganizationRoutesEnum.FormPageUpdate,
				params: { organizationId }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'OrganizationService:navigateToFormUpdate'
			);
		}
	}

	navigateToUsage( product: ProductEnum, organizationId: string ): void {
		if ( organizationId && product ) {
			const routeDefinition: OrganizationUsagePageRoute = {
				basePath: RouteBasePath.Organizations,
				path: OrganizationRoutesEnum.UsagePage,
				params: {
					product,
					organizationId,
					usagePeriod: getYearMonth()
				}
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'OrganizationService:navigateToUsage',
				false,
				false,
				true
			);
		}
	}

	navigateToDetailPageTab( organizationId: string, tab: AllowedDetailsTabEnum ) {
		if ( organizationId ) {
			const routeDefinition: OrganizationDetailPageTabRoute = {
				basePath: RouteBasePath.Organizations,
				path: OrganizationRoutesEnum.DetailPageTab,
				params: { organizationId, tab },
				replaceLastStoredRouteWithNew: true
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'OrganizationService:navigateToDetailPageTab'
			);
		}
	}

	navigateToDetailPageBusinessPremisesTabVariant( organizationId: string, variant: BusinessPremisesPresetEnum ) {
		if ( organizationId ) {
			const routeDefinition: OrganizationDetailPageBusinessPremisesTabVariantRoute = {
				basePath: RouteBasePath.Organizations,
				path: OrganizationRoutesEnum.DetailPageTabVariant,
				params: {
					organizationId,
					tab: AllowedDetailsTabEnum.ORGANIZATION_BUSINESS_PREMISES,
					variant
				},
				replaceLastStoredRouteWithNew: true
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'OrganizationService:navigateToDetailPageBusinessPremisesTabVariant'
			);
		}

	}

	navigateBack( caller = '', shouldRestoreListOperationData = true ): void {
		this.listOperationsService.state.listOperationsSetRestoreFlag(
			ListOperationsIdentifierEnum.ORGANIZATION_LIST_PAGE,
			shouldRestoreListOperationData,
			caller
		);

		this.routerService.navigateBack(
			this.routerService.getFallbackRoute( RouteBasePath.Organizations, OrganizationRoutesEnum.ListPage ),
			caller + ' | OrganizationService:navigateBack'
		);
	}


	// API #################################################################################
	// List Requests
	list( storeConfig?: IStoreConfig ): Observable<RequestCompleteListState<Organization>> {
		const storeProcessingConfig = this.createListStoreProcessingConfig<Organization>(
			storeConfig,
			this.storeList$<Organization>(),
			StoreActionsEnum.SET_ALL
		);

		const req$ = this._organizationApiService.list();
		return this.requestListThroughProxy( req$, storeProcessingConfig );
	}

	searchWithStoredListOperationsData(
		listOperationsId: ListOperationsIdentifierEnum,
		skipOrganizationActionItemChecks: boolean,
		caller: string
	): Observable<RequestCompleteListState<Organization>> {
		return this.listOperationsService.helper.requestWithStoredListOperationsData<Organization, IOrganizationHttpParams>(
			( [ listOperationsArg, callerArg, storeConfigArg, skipOrganizationActionItemChecksArg ] ) =>
				this.search( listOperationsArg, callerArg, storeConfigArg, skipOrganizationActionItemChecksArg ),
			[ `${ caller } | OrganizationService:searchWithStoredListOperationsData`, null, skipOrganizationActionItemChecks ],
			0,
			listOperationsId
		);
	}

	// TODO before providing storeConfig param a store search selector is needed
	//  for filtering store entries by params
	search(
		params?: IOrganizationHttpParams,
		caller?: string,
		storeConfig?: IStoreConfig,
		skipOrganizationActionItemChecks?: boolean,
		getAllPagesData?: boolean,
		httpContextConfig?: IHttpContextConfig
	): Observable<RequestCompleteListState<Organization>> {
		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<Organization>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<Organization>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | OrganizationService:search` };

		params = this.generateParams( params );
		const getAllPagesReq$ = ( [ paramsArg, httpContextConfigArg ] ) => this._organizationApiService.search( paramsArg, httpContextConfigArg );
		const req$ = getAllPagesData
								 ? this.requestAllPagesData<Organization>( getAllPagesReq$, [ params, httpContextConfig ], 0 )
								 : this._organizationApiService.search( params, httpContextConfig );

		const proxyReq$ = this.requestListThroughProxy<Organization>( req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( orgState: RequestCompleteListState<Organization> ) => {
			if ( orgState.requestState === RequestState.SUCCESS && !skipOrganizationActionItemChecks ) {
				orgState.list.forEach( org => this.checkAndUpdateEntityState( org, `${ caller } | OrganizationService:search->response` ) );
			}
		} );

		return proxyReq$;
	}

	// TODO before providing storeConfig param a store listChildren selector is needed
	//  for filtering store entries by params
	listChildren( org: Organization, params?: IOrganizationHttpParams ): Observable<RequestCompleteListState<Organization>> {
		// default storeConfig setup for listChildren as long as no listChildren selector exists
		const storeConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};
		const storeProcessingConfig = this.createListStoreProcessingConfig<Organization>(
			storeConfig,
			this.storeList$<Organization>(),
			StoreActionsEnum.UPSERT_MANY
		);

		params = this.generateParams( params );
		const req$ = this._organizationApiService.listChildren( org.id, params );
		return this.requestListThroughProxy( req$, storeProcessingConfig );
	}


	// Entity Requests
	read(
		options: {
			httpContextConfig?: IHttpContextConfig,
			id: string,
			skipOrganizationActionItemChecks?: boolean,
			storeConfig?: IStoreConfig,
			withFullResponse?: boolean
		} ): Observable<RequestCompleteEntityState<Organization>> {
		const {
			httpContextConfig,
			id,
			skipOrganizationActionItemChecks = true,
			withFullResponse
		} = options;

		let {
			storeConfig
		} = options;

		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Organization>(
			storeConfig,
			this.storeEntityById$<Organization>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._organizationApiService.read( id, { httpContextConfig, withFullResponse } );
		const proxyReq$ = this.requestEntityThroughProxy( id, req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( orgState: RequestCompleteEntityState<Organization> ) => {
			if ( orgState.requestState === RequestState.SUCCESS && !skipOrganizationActionItemChecks ) {
				this.checkAndUpdateEntityState( orgState.entity, `OrganizationService:read->response` );
			}
		} );

		return proxyReq$;
	}

	create( org: Organization, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Organization>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Organization>(
			storeConfig,
			null,
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._organizationApiService.create( org );
		return this.requestEntityThroughProxy( null, req$, storeProcessingConfig );
	}

	update( org: Organization, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Organization>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Organization>(
			storeConfig,
			this.storeEntityById$( org.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._organizationApiService.update( org );
		return this.requestEntityThroughProxy( org.id, req$, storeProcessingConfig );
	}

	private delete( organizationId: string, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Organization>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Organization>(
			storeConfig ?? defaultStoreConfig,
			null,
			StoreActionsEnum.REMOVE_ONE
		);

		const req$ = this._organizationApiService.delete( organizationId );
		return this.requestEntityThroughProxy( organizationId, req$, storeProcessingConfig );
	}

	private changeStatus(
		organization: Organization,
		status: OrganizationStatusEnum,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<Organization>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Organization>(
			storeConfig ?? defaultStoreConfig,
			this.storeEntityById$( organization.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._organizationApiService.changeStatus( { ...organization, status } );
		return this.requestEntityThroughProxy( organization.id, req$, storeProcessingConfig );
	}
}
